import React from "react";
import { Image } from "react-bootstrap";
import moment from "moment";
import { StoreHeaderProps, TimePeriod } from "../types";
import storeImage from "../../../assets/images/shop2.png";
import { getImageUrl } from "../../../Utils/imageUtils";

const PERIODS: TimePeriod[] = ["Today", "7 days", "30 days"];

const StoreHeader: React.FC<StoreHeaderProps> = ({
  storeName,
  storeLogo,
  timeFilter,
  isStoreOpen,
  openingTime,
  closingTime,
  onPeriodChange,
}) => {
  const formatTime = (timeStr?: string | null) => {
    if (!timeStr) return "";
    return moment(timeStr, "HH:mm:ss").format("h:mm A");
  };

  const hoursTooltip =
    openingTime && closingTime
      ? `Operating hours: ${formatTime(openingTime)} - ${formatTime(closingTime)}`
      : "Operating hours not set";

  return (
    <div className="bg-white rounded-4 border border-1 border-light-subtle p-3 p-md-4 d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4 shadow-sm">
      {/* Left: Store Logo & Name */}
      <div className="d-flex align-items-center gap-3">
        <Image
          src={
            storeLogo && storeLogo !== "null"
              ? getImageUrl(storeLogo)
              : storeImage
          }
          rounded
          className="flex-shrink-0 object-fit-cover shadow-sm"
          style={{ width: 56, height: 56, borderRadius: 14 }}
          alt="Store Logo"
        />
        <div>
          <div className="text-secondary small fw-medium">Hello,</div>
          <h2 className="fw-bold mb-0 text-dark fs-3 text-truncate">
            {storeName || "My Store"}
          </h2>
        </div>
      </div>

      {/* Right: Store open/closed status indicator & Time filter workaround */}
      <div className="d-flex flex-wrap align-items-center gap-3 ms-auto">
        {/* Store open/closed status pill matching custom design */}
        <div
          className="d-inline-flex align-items-center rounded-pill"
          style={{
            backgroundColor: isStoreOpen ? "#E5EEF9" : "#F1F5F9",
            padding: "6px 10px 6px 18px",
            gap: "14px",
            userSelect: "none",
            cursor: "default",
          }}
          title={hoursTooltip}
        >
          <span
            style={{
              color: "#101E18",
              fontSize: "15px",
              fontWeight: 500,
              letterSpacing: "-0.2px",
            }}
          >
            {isStoreOpen ? "Store open" : "Store closed"}
          </span>

          {/* Custom Switch Track matching the picture */}
          <div
            style={{
              width: "48px",
              height: "26px",
              borderRadius: "9999px",
              backgroundColor: isStoreOpen ? "#00DC54" : "#CBD5E1",
              display: "flex",
              alignItems: "center",
              padding: "3px",
              justifyContent: isStoreOpen ? "flex-end" : "flex-start",
              transition: "background-color 0.25s ease",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                width: "20px",
                height: "20px",
                borderRadius: "50%",
                backgroundColor: "#FFFFFF",
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.08)",
                transition: "transform 0.25s ease",
              }}
            />
          </div>
        </div>

        {/* Time Filter Pills Group */}
        <div
          className="p-1 rounded-pill d-inline-flex align-items-center"
          style={{ backgroundColor: "#E2E8F0" }}
        >
          {PERIODS.map((period) => (
            <button
              key={period}
              type="button"
              onClick={() => onPeriodChange(period)}
              className={`btn btn-sm border-0 rounded-pill px-3 py-1 fw-semibold small ${
                timeFilter === period
                  ? "bg-white text-dark shadow-sm"
                  : "text-secondary bg-transparent"
              }`}
              style={{ transition: "all 0.15s ease" }}
            >
              {period}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default React.memo(StoreHeader);
